import React, { memo, useEffect } from 'react'
import { Theme } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from '@material-ui/icons/CloseRounded'
import classNames from 'classnames'
import { useHistory, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import useMediaQuery from '@material-ui/core/useMediaQuery'

import { ReactComponent as Logo } from 'assets/images/logo.svg'
import styles from './styles'
import {
	LoginForm,
	SignUpForm,
	LinkedinLogin,
	ForgotPasswordForm,
	FacebookLogin,
	GoogleLogin,
} from './components'
import { AuthProvider, useAuthStateTest } from './reducer'
import { TAuthProps, EAuthSectionTypes, EEntity } from 'models'
import { EntityLogo } from 'components'

const useStyles = makeStyles(styles)

const AuthView: React.FunctionComponent<TAuthProps> = memo(
	({ section, temporaryUser }) => {
		// const { provider } = match.params
		const { provider }: { provider?: string } = useParams()
		const history = useHistory()
		const { t } = useTranslation()
		const theme: Theme = useTheme()
		const classes = useStyles()
		const goBack = () => history.goBack()
		const isSmDown = useMediaQuery(theme.breakpoints.down('sm'))
		useEffect(() => {
			const meta: any = document.querySelector('meta[name="theme-color"]')
			if (meta)
				meta.content =
					section === EAuthSectionTypes.login
						? theme.palette.primary.main
						: theme.palette.action.active
		}, [section, theme.palette.action.active, theme.palette.primary.main])

		let renderComponent: any
		if (provider === 'linkedin') {
			renderComponent = <LinkedinLogin redirect={true} />
		} else if (provider === 'google') {
			renderComponent = <GoogleLogin redirect={true} />
		} else if (provider === 'facebook') {
			renderComponent = <FacebookLogin redirect={true} />
		}

		useEffect(
			() => () => {
				// delete from session storage
				sessionStorage.removeItem('lastPageVisited')
			},
			[],
		)

		return provider ? (
			renderComponent
		) : (
			<div
				className={classNames(classes.container, {
					[classes.loginBackground]:
						section === EAuthSectionTypes.login ||
						section === EAuthSectionTypes.forgotPassword,
					[classes.signUpBackground]:
						section === EAuthSectionTypes.signUp,
				})}
			>
				<IconButton className={classes.closeButton} onClick={goBack}>
					<CloseIcon />
				</IconButton>
				<div className={classes.overlay} />
				{!temporaryUser && (
					<>
						<div className={classes.logo}>
							<Logo
								style={{
									width: isSmDown ? '45%' : '50%',
									marginBottom: '40px',
								}}
							/>
							<div className={classes.logoText}>
								Make mondays happy!
							</div>
						</div>
					</>
				)}
				{temporaryUser && (
					<div style={{ marginTop: 'auto', marginBottom: 'auto' }}>
						<div className={classes.temporaryUserMessage}>
							{t('Welcome back')}
						</div>
						<div
							style={{
								textAlign: 'center',
								fontSize: 24,
								fontWeight: 300,
								color: '#fff',
								marginTop: 10,
								marginBottom: 10,
							}}
						>
							{temporaryUser.firstName}
						</div>
						<EntityLogo
							type={EEntity.person}
							user={temporaryUser}
							extraAvatarClasses={classes.avatar}
						/>
					</div>
				)}
				{section === EAuthSectionTypes.login && <LoginForm />}
				{section === EAuthSectionTypes.signUp && <SignUpForm />}
				{section === EAuthSectionTypes.forgotPassword && (
					<ForgotPasswordForm />
				)}
			</div>
		)
	},
)

const Auth: React.FunctionComponent<any> = useAuthStateTest(AuthView, [
	'section',
	'temporaryUser',
])

export default ({ ...props }) => {
	const { section }: { section: EAuthSectionTypes } = props.match.params
	return (
		<AuthProvider initialSection={EAuthSectionTypes[section]}>
			<Auth {...props} />
		</AuthProvider>
	)
}
